// Ejemplos: actuadores, potencia y lógica.
import { at, part, wires, UNO } from './helpers.js';

const BB = { id: 'bb1', type: 'breadboard', x: 0, z: 80, rot: 0, props: { size: 'half' } };
const BB_FULL = { id: 'bb1', type: 'breadboard', x: 0, z: 85, rot: 0, props: { size: 'full' } };
const MINI = { id: 'bb2', type: 'breadboard', x: 165, z: 10, rot: 0, props: { size: 'mini' } };

// LEDs del 74HC595: cátodo en la columna c (con su resistencia al riel −) y ánodo en c + 1.
const SR_LED_COLS = [21, 24, 27, 30, 33, 36, 39, 42];
const SR_OUTS = [['QA', 'a6'], ['QB', 'h5'], ['QC', 'h6'], ['QD', 'h7'], ['QE', 'h8'], ['QF', 'h9'], ['QG', 'h10'], ['QH', 'h11']];
const SR_LED_COLORS = ['red', 'red', 'orange', 'yellow', 'yellow', 'green', 'green', 'blue'];

// Botones del 74HC165: terminal 1 en la columna c (a 5 V) y terminal 2 en c + 2 (a la entrada, con 10 kΩ a GND).
const IN_BUTTONS = [[12, 'b8', 'blue'], [16, 'b7', 'green'], [20, 'b6', 'yellow'], [24, 'b5', 'red']];

function railHole(col, size = 'full') {
  // Número de agujero del riel que cae en la columna dada (grupos de 5 con un hueco cada 6 columnas).
  const offset = size === 'full' ? 3 : 2;
  const idx = col - offset;
  return Math.floor(idx / 6) * 5 + (idx % 6) + 1;
}

export const EXAMPLES = [
  {
    id: 'rele-motor',
    category: 'Potencia',
    name: 'Relé: motor con su propia batería',
    description: 'Un módulo de relé (disparo en bajo) enciende y apaga un motor alimentado por 4 pilas AA. El diodo 1N4007 absorbe el pico del motor al abrir el contacto.',
    project: {
      name: 'Relé y motor',
      files: {
        'sketch.ino': `// Relé que enciende un motor de CD con su propia batería (4 pilas AA = 6 V).
// El módulo se activa con IN en LOW (disparo en bajo): COM pasa de NC a NO.
// El circuito del motor queda aislado del Arduino: solo el contacto del relé lo cierra.

const int RELE = 7;

void setup() {
  digitalWrite(RELE, HIGH);   // primero en HIGH para que el relé no se active al arrancar
  pinMode(RELE, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  digitalWrite(RELE, LOW);    // activa el relé: COM se une con NO y el motor recibe 6 V
  Serial.println("Motor encendido");
  delay(3000);
  digitalWrite(RELE, HIGH);   // suelta el relé: el motor se detiene poco a poco
  Serial.println("Motor apagado");
  delay(3000);
}
`,
      },
      parts: [
        UNO,
        part('rl', 'relay-module', { trigger: 'low' }, { x: 85, z: -45, rot: 0 }),
        part('bat', 'battery', { voltage: '6' }, { x: 75, z: 62, rot: 0 }),
        MINI,
        part('d1', 'diode', {}, at(MINI, 'e7', 'diode')),
        part('m1', 'dc-motor', { rotor: 'propeller' }, { x: 172, z: -62, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'rl:VCC', 'red'],
        ['uno:GND.1', 'rl:GND', 'black'],
        ['uno:7', 'rl:IN', 'yellow'],
        ['bat:+', 'rl:COM', 'red'],
        ['rl:NO', 'bb2:a11', 'orange'],
        ['bb2:c11', 'm1:+', 'red'],
        ['m1:-', 'bb2:c7', 'black'],
        ['bat:-', 'bb2:a7', 'black'],
      ]),
    },
  },
  {
    id: 'motor-l298n',
    category: 'Motores',
    name: 'Motor de CD con puente H L298N',
    description: 'El L298N cambia el sentido de giro (IN1/IN2) y la velocidad con PWM en ENA; también frena o deja el motor suelto. Batería de 9 V con tierra común.',
    project: {
      name: 'Motor con L298N',
      files: {
        'sketch.ino': `// Motor de CD con el puente H L298N: sentido con IN1/IN2 y velocidad con PWM en ENA.
// Batería de 9 V en +12V y GND del módulo; el GND del módulo va también al GND del Arduino.
// Se quitó el puente (jumper) de ENA para poder regular la velocidad.

const int ENA = 9;   // pin con PWM (~)
const int IN1 = 8;
const int IN2 = 7;

void setup() {
  pinMode(ENA, OUTPUT);
  pinMode(IN1, OUTPUT);
  pinMode(IN2, OUTPUT);
  Serial.begin(9600);
}

void girar(bool adelante, int velocidad) {
  digitalWrite(IN1, adelante ? HIGH : LOW);
  digitalWrite(IN2, adelante ? LOW : HIGH);
  analogWrite(ENA, velocidad);   // 0 a 255
}

void frenar() {
  digitalWrite(IN1, LOW);        // las dos salidas a GND: el motor frena en seco
  digitalWrite(IN2, LOW);
  analogWrite(ENA, 255);
}

void loop() {
  Serial.println("Adelante, acelerando");
  for (int v = 80; v <= 255; v += 5) {
    girar(true, v);
    delay(40);
  }
  delay(1000);
  Serial.println("Freno");
  frenar();
  delay(1000);
  Serial.println("Atras a media velocidad");
  girar(false, 150);
  delay(2000);
  Serial.println("Suelto (ENA en LOW): se detiene por inercia");
  analogWrite(ENA, 0);
  delay(2000);
}
`,
      },
      parts: [
        UNO,
        part('drv', 'l298n', { ena: false, enb: true, reg: true }, { x: 95, z: 5, rot: 0 }),
        part('bat', 'battery', { voltage: '9' }, { x: 32, z: 72, rot: 0 }),
        part('m1', 'dc-motor', { rotor: 'propeller' }, { x: 40, z: -78, rot: 180 }),
      ],
      wires: wires([
        ['uno:9', 'drv:ENA', 'orange'],
        ['uno:8', 'drv:IN1', 'yellow'],
        ['uno:7', 'drv:IN2', 'green'],
        ['uno:GND.1', 'drv:GND', 'black'],
        ['bat:+', 'drv:+12V', 'red'],
        ['bat:-', 'drv:GND', 'black'],
        ['drv:OUT1', 'm1:+', 'red'],
        ['drv:OUT2', 'm1:-', 'black'],
      ]),
    },
  },
  {
    id: 'motor-mosfet',
    category: 'Motores',
    name: 'Velocidad de un motor con MOSFET',
    description: 'La perilla fija el PWM de un MOSFET IRLZ44N que alimenta un motor desde una batería de 9 V. El diodo 1N4007 en paralelo protege al MOSFET.',
    project: {
      name: 'Motor con MOSFET',
      files: {
        'sketch.ino': `// Velocidad de un motor de CD con un MOSFET IRLZ44N y PWM.
// La perilla (A0) fija la velocidad. La compuerta va al pin 9 por una resistencia de 220 Ω
// y a GND por una de 10 kΩ (para que no quede al aire). El diodo 1N4007 en paralelo con el
// motor (franja hacia el +) deja circular la corriente de la bobina cuando el MOSFET se apaga.

const int MOSFET = 9;   // pin con PWM (~)
const int PERILLA = A0;

void setup() {
  pinMode(MOSFET, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(PERILLA);
  int pwm = map(lectura, 0, 1023, 0, 255);
  analogWrite(MOSFET, pwm);
  Serial.print("PWM: ");
  Serial.println(pwm);
  delay(100);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('q1', 'mosfet-n', {}, at(BB, 'f10', 'mosfet-n')),
        part('rg', 'resistor', { value: 220 }, at(BB, 'g6', 'resistor')),
        part('rpd', 'resistor', { value: 10000 }, at(BB, 'bn8', 'resistor', { rot: 90, pin: '1' })),
        part('d1', 'diode', {}, at(BB, 'i11', 'diode', { rot: 270 })),
        part('pot1', 'potentiometer', { value: 0.6 }, at(BB, 'e20', 'potentiometer', { rot: 180, pin: 'VCC' })),
        part('bat', 'battery', { voltage: '9' }, { x: -112, z: 92, rot: 0 }),
        part('m1', 'dc-motor', { rotor: 'propeller' }, { x: 98, z: 130, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp2', 'red'],
        ['uno:GND.1', 'bb1:tn3', 'black'],
        ['uno:GND.2', 'bb1:bn2', 'black'],
        ['bat:+', 'bb1:bp4', 'red'],
        ['bat:-', 'bb1:bn4', 'black'],
        ['uno:9', 'bb1:j6', 'orange'],
        ['bb1:j12', 'bb1:bn10', 'black'],
        ['m1:+', 'bb1:bp20', 'red'],
        ['m1:-', 'bb1:h11', 'blue'],
        ['bb1:a20', 'bb1:tp16', 'red'],
        ['bb1:a22', 'bb1:tn18', 'black'],
        ['bb1:a21', 'uno:A0', 'yellow'],
      ]),
    },
  },
  {
    id: 'transistores',
    category: 'Potencia',
    name: 'Transistores NPN y PNP como interruptores',
    description: 'Un 2N2222 (NPN) conmuta el lado de GND de un LED y un 2N2907 (PNP) el de +5 V. Fíjate en la lógica inversa del PNP.',
    project: {
      name: 'Transistores',
      files: {
        'sketch.ino': `// Transistores como interruptores.
// NPN 2N2222: la carga va entre +5 V y el colector; el emisor a GND. HIGH en la base lo enciende.
// PNP 2N2907: el emisor va a +5 V y la carga entre el colector y GND. LOW en la base lo enciende.
// Cada base lleva una resistencia de 1 kΩ para limitar su corriente (~4 mA).

const int BASE_NPN = 8;
const int BASE_PNP = 9;

void setup() {
  pinMode(BASE_NPN, OUTPUT);
  pinMode(BASE_PNP, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  digitalWrite(BASE_NPN, HIGH);  // NPN conduce: LED rojo encendido
  digitalWrite(BASE_PNP, HIGH);  // PNP cortado: LED verde apagado
  Serial.println("NPN encendido, PNP apagado");
  delay(1000);
  digitalWrite(BASE_NPN, LOW);   // NPN cortado
  digitalWrite(BASE_PNP, LOW);   // PNP conduce
  Serial.println("NPN apagado, PNP encendido");
  delay(1000);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('q1', 'npn-transistor', {}, at(BB, 'f8', 'npn-transistor')),
        part('rb1', 'resistor', { value: 1000 }, at(BB, 'g5', 'resistor')),
        part('led1', 'led', { color: 'red' }, at(BB, 'h10', 'led')),
        part('rc1', 'resistor', { value: 220 }, at(BB, 'i11', 'resistor')),
        part('q2', 'pnp-transistor', {}, at(BB, 'f19', 'pnp-transistor')),
        part('rb2', 'resistor', { value: 1000 }, at(BB, 'g20', 'resistor')),
        part('rc2', 'resistor', { value: 220 }, at(BB, 'i21', 'resistor')),
        part('led2', 'led', { color: 'green' }, at(BB, 'h25', 'led', { rot: 180, pin: 'A' })),
      ],
      wires: wires([
        ['uno:5V', 'bb1:bp1', 'red'],
        ['uno:GND.2', 'bb1:bn2', 'black'],
        ['uno:8', 'bb1:j5', 'yellow'],
        ['uno:9', 'bb1:j24', 'orange'],
        ['bb1:j15', 'bb1:bp12', 'red'],
        ['bb1:j8', 'bb1:bn6', 'black'],
        ['bb1:j19', 'bb1:bp16', 'red'],
        ['bb1:j26', 'bb1:bn21', 'black'],
      ]),
    },
  },
  {
    id: 'stepper-28byj48',
    category: 'Motores',
    name: 'Paso a paso 28BYJ-48 (Stepper)',
    description: 'La librería Stepper da una vuelta completa (2048 pasos) y media vuelta de regreso. Los LEDs A–D de la placa ULN2003 muestran qué bobinas están activas.',
    project: {
      name: 'Paso a paso 28BYJ-48',
      files: {
        'sketch.ino': `// Motor paso a paso 28BYJ-48 con su placa ULN2003 y la librería Stepper.
// 2048 pasos por vuelta (con la reductora). IN1–IN4 van a los pines 8, 9, 10 y 11, pero a la
// librería se le pasan en el orden 8, 10, 9, 11 para que la secuencia de bobinas sea la correcta.
#include <Stepper.h>

const int PASOS_POR_VUELTA = 2048;
Stepper motor(PASOS_POR_VUELTA, 8, 10, 9, 11);

void setup() {
  motor.setSpeed(12);   // rpm (el 28BYJ-48 llega a unas 15)
  Serial.begin(9600);
}

void loop() {
  Serial.println("Una vuelta en sentido horario");
  motor.step(PASOS_POR_VUELTA);
  delay(500);
  Serial.println("Media vuelta en sentido antihorario");
  motor.step(-PASOS_POR_VUELTA / 2);
  delay(500);
}
`,
      },
      parts: [UNO, part('st1', 'stepper-28byj48', {}, { x: 82, z: -8, rot: 0 })],
      wires: wires([
        ['uno:8', 'st1:IN1', 'blue'],
        ['uno:9', 'st1:IN2', 'purple'],
        ['uno:10', 'st1:IN3', 'yellow'],
        ['uno:11', 'st1:IN4', 'orange'],
        ['uno:5V', 'st1:VCC', 'red'],
        ['uno:GND.2', 'st1:GND', 'black'],
      ]),
    },
  },
  {
    id: 'stepper-accelstepper',
    category: 'Motores',
    name: 'Paso a paso 28BYJ-48 con aceleración',
    description: 'AccelStepper en medios pasos (4096 por vuelta): el motor acelera, frena al llegar y regresa.',
    project: {
      name: '28BYJ-48 con AccelStepper',
      files: {
        'sketch.ino': `// 28BYJ-48 con la librería AccelStepper en medios pasos: 4096 por vuelta.
// El motor acelera, frena al llegar a la posición y regresa al otro lado.
#include <AccelStepper.h>

// HALF4WIRE = medios pasos con 4 bobinas; pines en el orden IN1, IN3, IN2, IN4
AccelStepper motor(AccelStepper::HALF4WIRE, 8, 10, 9, 11);

void setup() {
  motor.setMaxSpeed(1000);      // medios pasos por segundo (unas 15 rpm)
  motor.setAcceleration(500);
  motor.moveTo(4096);           // una vuelta
  Serial.begin(9600);
}

void loop() {
  if (motor.distanceToGo() == 0) {
    Serial.print("Llegó a ");
    Serial.println(motor.currentPosition());
    motor.moveTo(-motor.currentPosition());
  }
  motor.run();
}
`,
      },
      parts: [UNO, part('st1', 'stepper-28byj48', {}, { x: 82, z: -8, rot: 0 })],
      wires: wires([
        ['uno:8', 'st1:IN1', 'blue'],
        ['uno:9', 'st1:IN2', 'purple'],
        ['uno:10', 'st1:IN3', 'yellow'],
        ['uno:11', 'st1:IN4', 'orange'],
        ['uno:5V', 'st1:VCC', 'red'],
        ['uno:GND.2', 'st1:GND', 'black'],
      ]),
    },
  },
  {
    id: 'nema17-a4988',
    category: 'Motores',
    name: 'NEMA 17 con driver A4988',
    description: 'AccelStepper en modo DRIVER (STEP y DIR) con micropasos de 1/4: dos vueltas en un sentido y de regreso. VMOT con una batería de 12 V.',
    project: {
      name: 'NEMA 17 con A4988',
      files: {
        'sketch.ino': `// Motor NEMA 17 con driver A4988 y la librería AccelStepper (modo DRIVER: un pin de pasos y uno
// de sentido). Micropasos de 1/4 (interruptores MS1–MS3): 200 × 4 = 800 pulsos por vuelta.
// VMOT con una batería de 12 V, VDD con los 5 V del Arduino y EN en LOW para habilitar el motor.
#include <AccelStepper.h>

const int PIN_STEP = 3;
const int PIN_DIR = 2;
const int PIN_EN = 8;
const long PULSOS_POR_VUELTA = 200L * 4;

AccelStepper motor(AccelStepper::DRIVER, PIN_STEP, PIN_DIR);

void setup() {
  pinMode(PIN_EN, OUTPUT);
  digitalWrite(PIN_EN, LOW);           // habilita el driver
  motor.setMaxSpeed(1600);             // pulsos por segundo (2 vueltas por segundo)
  motor.setAcceleration(1200);
  motor.moveTo(PULSOS_POR_VUELTA * 2); // dos vueltas en sentido horario
  Serial.begin(9600);
}

void loop() {
  if (motor.distanceToGo() == 0) {
    Serial.print("Posicion: ");
    Serial.println(motor.currentPosition());
    delay(500);
    motor.moveTo(-motor.currentPosition());
  }
  motor.run();
}
`,
      },
      parts: [
        UNO,
        part('nm', 'stepper-nema17', { micro: '4' }, { x: 98, z: 0, rot: 0 }),
        part('bat', 'battery', { voltage: '12' }, { x: 30, z: 88, rot: 0 }),
      ],
      wires: wires([
        ['uno:3', 'nm:STEP', 'yellow'],
        ['uno:2', 'nm:DIR', 'green'],
        ['uno:8', 'nm:EN', 'blue'],
        ['uno:5V', 'nm:VDD', 'red'],
        ['uno:GND.2', 'nm:GND', 'black'],
        ['bat:+', 'nm:VMOT', 'red'],
        ['bat:-', 'nm:GND.2', 'black'],
      ]),
    },
  },
  {
    id: '74hc595-leds',
    category: 'Lógica',
    name: '8 LEDs con un 74HC595',
    description: 'Con shiftOut() y solo 3 pines (datos, reloj y «latch») el registro de desplazamiento controla 8 LEDs: luz que va y viene y un contador binario.',
    project: {
      name: '74HC595 y 8 LEDs',
      files: {
        'sketch.ino': `// 8 LEDs con un registro de desplazamiento 74HC595 y shiftOut().
// Solo 3 pines del Arduino: datos (SER), reloj (SRCLK) y «latch» (RCLK).
// VCC y SRCLR van a 5 V; GND y OE a GND. QA es el bit 0 (el LED de la izquierda).

const int DATOS = 11;  // SER, pin 14 del chip
const int RELOJ = 12;  // SRCLK, pin 11
const int LATCH = 8;   // RCLK, pin 12

void escribir(byte valor) {
  digitalWrite(LATCH, LOW);
  shiftOut(DATOS, RELOJ, MSBFIRST, valor);
  digitalWrite(LATCH, HIGH);   // las 8 salidas cambian juntas
}

void setup() {
  pinMode(DATOS, OUTPUT);
  pinMode(RELOJ, OUTPUT);
  pinMode(LATCH, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  // Luz que va y viene
  for (int i = 0; i < 8; i++) {
    escribir(1 << i);
    delay(80);
  }
  for (int i = 6; i > 0; i--) {
    escribir(1 << i);
    delay(80);
  }
  // Contador binario
  for (int n = 0; n < 32; n++) {
    escribir(n);
    Serial.println(n, BIN);
    delay(150);
  }
}
`,
      },
      parts: [
        UNO,
        BB_FULL,
        part('sr1', '74hc595', {}, at(BB_FULL, 'f5', '74hc595')),
        ...SR_LED_COLS.flatMap((c, i) => [
          part(`led${i + 1}`, 'led', { color: SR_LED_COLORS[i] }, at(BB_FULL, `f${c}`, 'led')),
          part(`r${i + 1}`, 'resistor', { value: 220 }, at(BB_FULL, `bn${railHole(c)}`, 'resistor', { rot: 90, pin: '1' })),
        ]),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp1', 'red'],
        ['uno:GND.1', 'bb1:tn2', 'black'],
        ['uno:GND.2', 'bb1:bn2', 'black'],
        ['bb1:a5', 'bb1:tp3', 'red'],
        ['bb1:a11', 'bb1:tp8', 'red'],
        ['bb1:a8', 'bb1:tn5', 'black'],
        ['bb1:j12', 'bb1:bn9', 'black'],
        ['uno:11', 'bb1:b7', 'green'],
        ['uno:8', 'bb1:b9', 'yellow'],
        ['uno:12', 'bb1:b10', 'blue'],
        ...SR_OUTS.map(([, hole], i) => [`bb1:${hole}`, `bb1:i${SR_LED_COLS[i] + 1}`, 'orange']),
      ]),
    },
  },
  {
    id: 'l293d-motor',
    category: 'Motores',
    name: 'Motor de CD con L293D',
    description: 'El chip L293D en la protoboard mueve un motor en ambos sentidos con PWM en EN12. Motores con 4 pilas AA (VS) y lógica con los 5 V del Arduino (VSS).',
    project: {
      name: 'Motor con L293D',
      files: {
        'sketch.ino': `// Motor de CD con el puente H L293D (DIP-16).
// IN1 (pin 2 del chip) e IN2 (pin 7) dan el sentido; EN12 (pin 1) la velocidad con PWM.
// VSS (pin 16) a 5 V; VS (pin 8) a la batería de 6 V; GND (pines 4, 5, 12, 13) común con el Arduino.

const int EN12 = 9;   // pin con PWM (~)
const int IN1 = 8;
const int IN2 = 7;

void setup() {
  pinMode(EN12, OUTPUT);
  pinMode(IN1, OUTPUT);
  pinMode(IN2, OUTPUT);
  Serial.begin(9600);
}

void motor(int velocidad) {        // -255 a 255: el signo es el sentido
  digitalWrite(IN1, velocidad > 0 ? HIGH : LOW);
  digitalWrite(IN2, velocidad < 0 ? HIGH : LOW);
  analogWrite(EN12, abs(velocidad));
}

void loop() {
  for (int v = 0; v <= 255; v += 15) {
    motor(v);
    delay(100);
  }
  Serial.println("Máxima velocidad");
  delay(1500);
  motor(0);
  delay(800);
  Serial.println("Al revés");
  motor(-200);
  delay(2000);
  motor(0);
  delay(800);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('ic1', 'l293d', {}, at(BB, 'f10', 'l293d')),
        part('bat', 'battery', { voltage: '6' }, { x: -115, z: 88, rot: 0 }),
        part('m1', 'dc-motor', { rotor: 'propeller' }, { x: 96, z: 132, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp1', 'red'],
        ['uno:GND.1', 'bb1:tn1', 'black'],
        ['uno:GND.2', 'bb1:bn1', 'black'],
        ['bat:+', 'bb1:bp1', 'red'],
        ['bat:-', 'bb1:bn3', 'black'],
        ['bb1:a10', 'bb1:tp8', 'red'],
        ['bb1:j17', 'bb1:bp14', 'red'],
        ['bb1:j14', 'bb1:bn11', 'black'],
        ['bb1:a14', 'bb1:tn11', 'black'],
        ['uno:9', 'bb1:j10', 'orange'],
        ['uno:8', 'bb1:j11', 'yellow'],
        ['uno:7', 'bb1:j16', 'green'],
        ['m1:+', 'bb1:h12', 'red'],
        ['m1:-', 'bb1:h15', 'black'],
      ]),
    },
  },
  {
    id: '74hc165-botones',
    category: 'Lógica',
    name: 'Leer botones con un 74HC165',
    description: 'Un registro 74HC165 lee 8 entradas con 3 pines: 4 botones (teclas 1 a 4) y 4 entradas fijas. El monitor serie muestra los 8 bits cuando cambian.',
    project: {
      name: '74HC165 y botones',
      files: {
        'sketch.ino': `// Lee 8 entradas con un registro 74HC165 usando solo 3 pines del Arduino.
// A–D: botones con resistencia de 10 kΩ a GND (presiónalos o usa las teclas 1, 2, 3 y 4).
// E y G van a 5 V; F y H a GND. CLK INH y SER a GND.

const int CARGAR = 8;   // SH/LD (pin 1): en LOW copia las entradas
const int RELOJ = 12;   // CLK (pin 2)
const int DATOS = 11;   // QH (pin 9)

byte leer() {
  digitalWrite(CARGAR, LOW);   // copia A–H al registro
  delayMicroseconds(5);
  digitalWrite(CARGAR, HIGH);
  byte valor = 0;
  for (int i = 7; i >= 0; i--) {     // sale primero H y al final A
    if (digitalRead(DATOS)) valor |= (1 << i);
    digitalWrite(RELOJ, HIGH);       // desplaza: aparece la siguiente entrada
    digitalWrite(RELOJ, LOW);
  }
  return valor;
}

void setup() {
  pinMode(CARGAR, OUTPUT);
  pinMode(RELOJ, OUTPUT);
  pinMode(DATOS, INPUT);
  digitalWrite(CARGAR, HIGH);
  Serial.begin(9600);
  Serial.println("HGFEDCBA");
}

byte anterior = 0xFF;

void loop() {
  byte valor = leer();
  if (valor != anterior) {
    anterior = valor;
    for (int i = 7; i >= 0; i--) Serial.print(bitRead(valor, i));
    Serial.println();
  }
  delay(20);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('ic1', '74hc165', {}, at(BB, 'f3', '74hc165')),
        ...IN_BUTTONS.flatMap(([c, , color], i) => [
          part(`btn${i + 1}`, 'pushbutton', { color, key: String(i + 1) }, at(BB, `e${c}`, 'pushbutton')),
          part(`r${i + 1}`, 'resistor', { value: 10000 }, at(BB, `bn${railHole(c + 2, 'half')}`, 'resistor', { rot: 90, pin: '1' })),
        ]),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp1', 'red'],
        ['uno:5V', 'bb1:bp1', 'red'],
        ['uno:GND.1', 'bb1:tn1', 'black'],
        ['uno:GND.2', 'bb1:bn1', 'black'],
        ['bb1:a3', 'bb1:tp2', 'red'],
        ['bb1:a4', 'bb1:tn3', 'black'],
        ['bb1:a9', 'bb1:tn7', 'black'],
        ['bb1:j10', 'bb1:bn8', 'black'],
        ['uno:8', 'bb1:j3', 'yellow'],
        ['uno:12', 'bb1:j4', 'blue'],
        ['bb1:a10', 'uno:11', 'green'],
        ['bb1:j5', 'bb1:bp4', 'red'],
        ['bb1:j6', 'bb1:bn5', 'black'],
        ['bb1:j7', 'bb1:bp6', 'red'],
        ['bb1:j8', 'bb1:bn6', 'black'],
        ...IN_BUTTONS.flatMap(([c, input]) => [
          [`bb1:a${c}`, `bb1:tp${railHole(c, 'half')}`, 'red'],
          [`bb1:i${c + 2}`, `bb1:${input}`, 'orange'],
        ]),
      ]),
    },
  },
];
